import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import { Plus, X, Calendar, ChevronLeft, ChevronRight, Clock, AlertTriangle } from 'lucide-react';

const TYPES_ABSENCE = [
  { value: 'conges_payes', label: 'Congés payés', color: '#22c55e' },
  { value: 'conges_anciennete', label: 'Congés ancienneté', color: '#06b6d4' },
  { value: 'conges_enfant_malade', label: 'Congés enfant malade', color: '#a78bfa' },
  { value: 'maladie', label: 'Arret maladie', color: '#f97316' },
  { value: 'maternite', label: 'Conge maternite', color: '#ec4899' },
  { value: 'at', label: 'Accident du travail', color: '#ef4444' },
  { value: 'conge_sans_solde', label: 'Conge sans solde', color: '#6b7280' },
  { value: 'conges_speciaux', label: 'Congés spéciaux', color: '#8b5cf6' },
  { value: 'absence_injustifiee', label: 'Absence injustifiee', color: '#dc2626' },
];

const Field = ({ label, children }) => (
  <div><label className="text-slate-400 text-xs font-medium mb-1 block">{label}</label>{children}</div>
);
const Input = (props) => <input {...props} className={`w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A] ${props.className || ''}`} />;

export default function AbsencesPage() {
  const { mpFetch } = useMoanaPaye();
  const [tab, setTab] = useState('absences');
  const [entreprises, setEntreprises] = useState([]);
  const [selectedEnt, setSelectedEnt] = useState('');
  const [absences, setAbsences] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [salaries, setSalaries] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [planningData, setPlanningData] = useState(null);
  const [planningMois, setPlanningMois] = useState(new Date().getMonth() + 1);
  const [planningAnnee, setPlanningAnnee] = useState(2026);

  useEffect(() => {
    mpFetch('/api/moana-paye/entreprises').then(d => { setEntreprises(d); if (d.length) setSelectedEnt(d[0].id); }).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedEnt) {
      mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/absences`).then(setAbsences).catch(console.error);
      mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/salaries`).then(setSalaries).catch(console.error);
    }
  }, [selectedEnt]);

  useEffect(() => {
    if (selectedEnt && tab === 'planning') {
      mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/planning?mois=${planningMois}&annee=${planningAnnee}`).then(setPlanningData).catch(console.error);
    }
  }, [selectedEnt, tab, planningMois, planningAnnee]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => {
    setForm({ salarie_id: '', entreprise_id: selectedEnt, type_absence: 'conges_payes', date_debut: '', date_fin: '', numero_arret_annee: 1, observations: '', demande_par: 'gestionnaire' });
    setShowForm(true);
  };

  const saveAbsence = async () => {
    setSaving(true);
    try {
      await mpFetch('/api/moana-paye/absences', { method: 'POST', body: JSON.stringify(form) });
      setShowForm(false);
      mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/absences`).then(setAbsences);
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const getTypeInfo = (t) => TYPES_ABSENCE.find(a => a.value === t) || { label: t, color: '#6b7280' };

  const prevMonth = () => {
    if (planningMois === 1) { setPlanningMois(12); setPlanningAnnee(a => a - 1); }
    else setPlanningMois(m => m - 1);
  };
  const nextMonth = () => {
    if (planningMois === 12) { setPlanningMois(1); setPlanningAnnee(a => a + 1); }
    else setPlanningMois(m => m + 1);
  };

  const MOIS_NOMS = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

  return (
    <div data-testid="mp-absences">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Absences & Conges</h2>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-[#1a2332] rounded-lg p-1 border border-white/10">
            <button onClick={() => setTab('absences')} className={`px-3 py-1.5 text-xs rounded-md ${tab === 'absences' ? 'bg-[#2D5A5A] text-white' : 'text-slate-400'}`}>Absences</button>
            <button onClick={() => setTab('planning')} className={`px-3 py-1.5 text-xs rounded-md ${tab === 'planning' ? 'bg-[#2D5A5A] text-white' : 'text-slate-400'}`}>Planning</button>
          </div>
          {tab === 'absences' && (
            <button onClick={openNew} className="flex items-center gap-2 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="mp-abs-new">
              <Plus className="w-4 h-4" /> Nouvelle absence
            </button>
          )}
        </div>
      </div>

      {/* TAB: Absences list */}
      {tab === 'absences' && (
        <div className="space-y-2">
          {absences.length === 0 ? (
            <div className="bg-[#1a2332] border border-white/10 rounded-xl p-10 text-center">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Aucune absence enregistree</p>
            </div>
          ) : absences.map(a => {
            const info = getTypeInfo(a.type_absence);
            return (
              <div key={a.id} className="bg-[#1a2332] border border-white/10 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-1 h-10 rounded-full" style={{ backgroundColor: info.color }} />
                  <div>
                    <div className="text-white text-sm font-medium">{a.prenom} {a.nom}</div>
                    <div className="text-slate-500 text-xs">{info.label} &middot; {a.date_debut} au {a.date_fin} &middot; {a.nb_jours_ouvres}j decomptes ({a.nb_jours_calendaires}j cal.)</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.impact_anciennete === 'suspendu' && <span className="text-xs bg-orange-500/15 text-orange-400 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Anc. susp.</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    a.statut === 'validee' ? 'bg-green-500/15 text-green-400' :
                    a.statut === 'demande' ? 'bg-[#E8A020]/15 text-[#E8A020]' :
                    a.statut === 'refusee' ? 'bg-red-500/15 text-red-400' :
                    'bg-white/10 text-slate-400'
                  }`}>{a.statut}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* TAB: Planning */}
      {tab === 'planning' && planningData && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="text-slate-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>
            <span className="text-white font-semibold">{MOIS_NOMS[planningMois]} {planningAnnee}</span>
            <button onClick={nextMonth} className="text-slate-400 hover:text-white"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-3 py-2 text-slate-500 font-medium sticky left-0 bg-[#1a2332] z-10 min-w-[140px]">Salarie</th>
                  {planningData.presents_par_jour.map((j, i) => {
                    const d = new Date(planningAnnee, planningMois - 1, j.jour);
                    const jourSemaine = ['D','L','M','M','J','V','S'][d.getDay()];
                    return (
                      <th key={i} className={`px-1 py-2 text-center min-w-[28px] ${j.weekend ? 'bg-white/3 text-slate-600' : 'text-slate-400'}`}>
                        <div>{jourSemaine}</div>
                        <div>{j.jour}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {planningData.planning.map((row, ri) => (
                  <tr key={ri} className="border-b border-white/5">
                    <td className="px-3 py-1.5 text-white sticky left-0 bg-[#1a2332] z-10 truncate">{row.salarie.prenom} {row.salarie.nom}</td>
                    {row.jours.map((j, ji) => {
                      let bg = 'transparent';
                      let title = '';
                      if (j.status === 'weekend') bg = 'rgba(255,255,255,0.03)';
                      else if (j.status === 'ferie') { bg = 'rgba(232,160,32,0.15)'; title = 'Ferie'; }
                      else if (j.status === 'absent') {
                        const info = getTypeInfo(j.type_absence);
                        bg = info.color + '40';
                        title = info.label;
                      }
                      return <td key={ji} className="px-0.5 py-1.5 text-center" title={title}><div className="w-5 h-5 rounded-sm mx-auto" style={{ backgroundColor: bg }} /></td>;
                    })}
                  </tr>
                ))}
                <tr className="border-t border-white/10">
                  <td className="px-3 py-1.5 text-slate-500 font-medium sticky left-0 bg-[#1a2332] z-10">Presents</td>
                  {planningData.presents_par_jour.map((j, i) => (
                    <td key={i} className={`px-0.5 py-1.5 text-center font-mono ${j.weekend ? 'text-slate-700' : 'text-[#2D5A5A]'}`}>{j.weekend ? '-' : j.presents}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {/* Legende */}
          <div className="flex flex-wrap gap-3 mt-3">
            {TYPES_ABSENCE.slice(0, 6).map(t => (
              <div key={t.value} className="flex items-center gap-1.5 text-xs text-slate-400">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.color + '60' }} />
                {t.label}
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <div className="w-3 h-3 rounded-sm bg-[#E8A020]/30" /> Ferie
            </div>
          </div>
        </div>
      )}

      {/* Modal new absence */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Nouvelle absence</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Salarié">
                <select value={form.salarie_id} onChange={e => set('salarie_id', e.target.value)} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="">Selectionner</option>
                  {salaries.map(s => <option key={s.id} value={s.id}>{s.prenom} {s.nom}</option>)}
                </select>
              </Field>
              <Field label="Type d'absence">
                <select value={form.type_absence} onChange={e => set('type_absence', e.target.value)} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                  {TYPES_ABSENCE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date début"><Input type="date" value={form.date_debut} onChange={e => set('date_debut', e.target.value)} /></Field>
                <Field label="Date fin"><Input type="date" value={form.date_fin} onChange={e => set('date_fin', e.target.value)} /></Field>
              </div>
              {form.type_absence === 'maladie' && (
                <Field label="N de l'arret (annee civile)"><Input type="number" min={1} value={form.numero_arret_annee} onChange={e => set('numero_arret_annee', parseInt(e.target.value))} /></Field>
              )}
              <Field label="Observations"><Input value={form.observations || ''} onChange={e => set('observations', e.target.value)} placeholder="Notes..." /></Field>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-400">Annuler</button>
              <button onClick={saveAbsence} disabled={saving || !form.salarie_id || !form.date_debut || !form.date_fin} className="px-4 py-2 bg-[#2D5A5A] text-white text-sm rounded-lg disabled:opacity-50" data-testid="mp-abs-save">{saving ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
